import { useCallback, useState } from "react";
import { useCommunication } from "@/contexts/chat-provider";
import { useTenant } from "@/contexts/tenant-provider";
import { toast } from "@/lib/use-toast";
import { generateConnectLinkMessage } from "@/lib/wearable-connect";
import { MessageKind } from "@/types/enums/message";
import type { PatientSummary } from "@/types/patient";
import type { WearableConnectSession } from "@/types/wearable";
import { createPatientWearableSharedConnectSession } from "@/api/provider/user/patient/wearable";
import { useMessageDraftStore } from "@/stores/message-draft-store";
export interface ConnectLinkTextResult {
  session: WearableConnectSession;
  /** Set only when the message actually landed in a chat as a draft. */
  chatId?: string;
}

/**
 * Handing a patient a link to connect their own device, by text or by email.
 *
 * Modelled on `useShareQuote`, and its failure rule is the one that shapes this:
 * minting throws, and nothing after it does. A retry past that point would mint
 * a SECOND aggregator session — harmless locally, since no row is written until
 * the device is authorised, but real quota at the aggregator and a second live
 * bearer link for the same patient.
 *
 * Nothing here sends. Both paths end with a message in front of staff to read
 * and send themselves.
 */
export function useSendConnectLink() {
  const {
    openChat
  } = useCommunication();
  const {
    tenant
  } = useTenant();
  const {
    handleDraftChange
  } = useMessageDraftStore();
  const [isSending, setIsSending] = useState(false);

  // The only step that throws. Callers may retry this safely; nothing has been
  // minted when it fails.
  const createConnectLink = useCallback(async (patientId: string): Promise<WearableConnectSession> => {
    try {
      const session = await createPatientWearableSharedConnectSession(patientId);
      if (!session) throw new Error("Failed to create the connect link");
      return session;
    } catch (err) {
      toast({
        title: "Couldn't create the link",
        description: "Please try again.",
        variant: "error"
      });
      throw err;
    }
  }, []);
  const sendConnectLinkByText = useCallback(async (patient: PatientSummary): Promise<ConnectLinkTextResult> => {
    setIsSending(true);
    try {
      const session = await createConnectLink(patient.id);

      // `openChat` cannot create a chat — a patient with no chat row makes it
      // raise its own toast and resolve without running the callback — so the
      // callback firing is the only proof a draft actually landed. The caller
      // shows the link itself when it did not, rather than stranding a session
      // nobody can reach.
      const message = generateConnectLinkMessage(patient.firstName, session, tenant?.name);
      let chatId: string | undefined = undefined;
      try {
        await openChat(patient.id, undefined, openedChatId => {
          handleDraftChange(openedChatId, message, {
            kind: MessageKind.DEFAULT
          });
          chatId = openedChatId;
        });
      } catch {
        // openChat raises its own error toast; the caller handles the rest.
      }
      if (chatId) {
        toast({
          title: "Link ready to send",
          description: "Review the message in chat and send it.",
          variant: "success"
        });
      }
      return {
        session,
        chatId
      };
    } finally {
      setIsSending(false);
    }
  }, [createConnectLink, openChat, tenant, handleDraftChange]);
  return {
    createConnectLink,
    sendConnectLinkByText,
    isSending
  };
}